// PARTS
import Table from 'parts/table/Table.js';

// lOCAL VARIABLES
import {
  DETAILS_STATUS,
  EMISSION_STATUS,
  HOMENET_STATUS,
  IMV_STATUS,
  INSPECTION_STATUS,
  INVENTORY_LOCATION,
  KEY_STATUS,
  PICTURE_STATUS,
  SPARE_KEY_STATUS,
  WARRANTY_STATUS,
  MISC_STATUS
} from './codes.js';
import { COLUMNS as GLOBAL_COLUMNS } from 'columns.js';
import { INTERFACE } from 'pathnames.js';

// LOCAL COMPONENTS
import IMVLabel from './tools/IMVLabel.js';
import PriceAdjustment from './tools/PriceAdjustment.js';
import PriceAfterAdjustment from './tools/PriceAfterAdjustment.js';
import Gross from './tools/Gross.js';

const inventoryLink = (row, anchor) => {
  return `/${INTERFACE.inventory}/view/${row.daily_sales_inventory_id}#${anchor}`;
};

// EXPORTS
export const COLUMNS = {
  age: {
    key: 'age',
    label: 'Age',
    tooltip: 'Inventory Age',
    width: 45
  },
  location: {
    component: Table.Code,
    key: 'inventory_location',
    label: 'LOC',
    tooltip: 'Inventory Location',
    codes: INVENTORY_LOCATION,
    className: 'text-center',
    width: 45
  },
  purchasedDate: {
    ...GLOBAL_COLUMNS.purchasedDate,
    key: 'purchase_date',
    width: 65
  },
  retailWholesale: {
    ...GLOBAL_COLUMNS.retailWholesale,
    key: 'sales_type'
  },
  dmsInvoice: {
    component: ({ value }) => value !== null && <Table.Dollars>{value}</Table.Dollars>,
    key: 'dms_invoice',
    label: 'DMS Inv',
    tooltip: 'DMS Invoice',
    width: 85
  },

  // PRICES
  advertisedPrice: {
    component: ({ value }) => value !== null && <Table.Dollars>{value}</Table.Dollars>,
    key: 'advertised_price',
    label: 'Advertised',
    tooltip: 'Advertised Price',
    width: 85
  },

  // IMV
  imvEngine: {
    component: Table.Code,
    key: 'imv_engine',
    label: 'IMV Engine',
    codes: IMV_STATUS,
    width: 60
  },
  imvLabel: {
    component: IMVLabel,
    key: 'imv_label',
    label: 'IMV Label',
    width: 75
  },
  imvOptions: {
    component: Table.Code,
    key: 'imv_options',
    label: 'IMV Options',
    codes: IMV_STATUS,
    width: 60
  },
  imvTrim: {
    component: Table.Code,
    key: 'imv_trim',
    label: 'IMV Trim',
    codes: IMV_STATUS,
    width: 60
  },
  imvTrans: {
    component: Table.Code,
    key: 'imv_trans',
    label: 'IMV Trans',
    codes: IMV_STATUS,
    width: 60
  },
  imvUpdated: {
    component: Table.Date,
    key: 'imv_updated',
    label: 'Last IMV Update',
    width: 80
  },

  // IMV PRICES
  imvHighPrice: {
    component: ({ value }) => value !== null && <Table.Dollars>{value}</Table.Dollars>,
    key: 'imv_high_price',
    label: 'IMV High',
    tooltip: 'IMV High Price',
    width: 85
  },
  gross: {
    component: Gross,
    key: 'gross',
    label: 'Gross',
    tooltip: 'Gross = Advertised Price - DMS Invoice',
    width: 85
  },
  imvFairPrice: {
    component: ({ value }) => value !== null && <Table.Dollars>{value}</Table.Dollars>,
    key: 'imv_fair_price',
    label: 'IMV Fair',
    tooltip: 'IMV Fair Price',
    width: 85
  },
  imvGoodPrice: {
    component: ({ value }) => value !== null && <Table.Dollars>{value}</Table.Dollars>,
    key: 'imv_good_price',
    label: 'IMV Good',
    tooltip: 'IMV Good Price',
    width: 85
  },
  imvGreatPrice: {
    component: ({ value }) => value !== null && <Table.Dollars>{value}</Table.Dollars>,
    key: 'imv_great_price',
    label: 'IMV Great',
    tooltip: 'IMV Great Price',
    width: 85
  },
  imvFairDifference: {
    component: ({ value }) => value !== null && <Table.Dollars>{value}</Table.Dollars>,
    key: 'imv_fair_price_difference',
    label: '+/- IMV Fair',
    tooltip: '+/- IMV Fair Price',
    width: 85
  },
  imvPrice: {
    component: Table.Dollars,
    key: 'imv_price',
    label: 'IMV Price',
    width: 85
  },

  // PRICE ADJUSTMENT
  priceAdjustmnet: {
    component: PriceAdjustment,
    key: 'adjustment_price',
    label: '+/- Adjustment',
    sortable: false,
    mobileStack: true,
    width: 325,
    disableResizing: true
  },
  priceAfterAdjustmnet: {
    component: PriceAfterAdjustment,
    key: 'adjusted_price',
    label: 'Price After Adjustment',
    sortable: false,
    mobileStack: true,
    width: 125,
    disableResizing: true
  },

  // STATUSES
  warranty: {
    component: Table.Code,
    key: 'warranty',
    label: 'WT',
    tooltip: 'Warranty Type',
    codes: WARRANTY_STATUS,
    className: 'text-center',
    width: 40
  },
  key: {
    component: Table.Code,
    key: 'key_status_code',
    label: 'KEY',
    tooltip: 'Key Info',
    codes: KEY_STATUS,
    className: 'text-center',
    to: row => inventoryLink(row, 'keyInfo'),
    width: 45
  },
  spareKey: {
    component: Table.Code,
    key: 'spare_key_status_code',
    label: 'SPR',
    tooltip: 'Spare Key',
    codes: SPARE_KEY_STATUS,
    className: 'text-center',
    to: row => inventoryLink(row, 'spareKey'),
    width: 45
  },
  homenet: {
    component: Table.Code,
    key: 'homenet_status_code',
    label: 'HNT',
    tooltip: 'Homenet',
    codes: HOMENET_STATUS,
    className: 'text-center',
    to: row => inventoryLink(row, 'homenet'),
    width: 45
  },
  detail: {
    component: Table.Code,
    key: 'detail_status_code',
    label: 'DTL',
    tooltip: 'Detail & Cleanup Status',
    codes: DETAILS_STATUS,
    className: 'text-center',
    to: row => inventoryLink(row, 'detailCleanUp'),
    width: 45
  },
  inspection: {
    component: Table.Code,
    key: 'inspection_status_code',
    label: 'INS',
    tooltip: 'Inspection Status',
    codes: INSPECTION_STATUS,
    className: 'text-center',
    to: row => inventoryLink(row, 'inspection'),
    width: 45
  },
  emission: {
    component: Table.Code,
    key: 'emission_status_code',
    label: 'EMI',
    tooltip: 'Emission Status',
    codes: EMISSION_STATUS,
    className: 'text-center',
    to: row => inventoryLink(row, 'emission'),
    width: 45
  },
  picture: {
    component: Table.Code,
    key: 'picture_status_code',
    label: 'PIC',
    tooltip: 'Picture Status',
    codes: PICTURE_STATUS,
    className: 'text-center',
    to: row => inventoryLink(row, 'photo'),
    width: 45
  },
  miscellaneous: {
    component: Table.Code,
    key: 'miscellaneous_status_code',
    label: 'MSC',
    tooltip: 'Miscellaneous Status',
    codes: MISC_STATUS,
    className: 'text-center',
    to: row => inventoryLink(row, 'misc'),
    width: 45
  }
};
